import { useState } from 'react'

import { ApiError } from '../../lib/api'
import { biz, type Contact, type Product } from '../../lib/business'
import { useToast } from '../../app/Toasts'

/** Sentinel value for the "add a new one" option in a picker.
 *  Distinct from '' so an empty selection still means "nothing chosen". */
export const ADD_NEW = '__add_new__'

const GST_RATES = [0, 0.25, 3, 5, 12, 18, 28]
const UNITS = ['Nos', 'Pcs', 'Kg', 'Ltr', 'Mtr', 'Box', 'Set', 'Hrs', 'Days']

/** Create a customer without leaving the document you are writing.
 *
 *  Having to go to Contacts, create, come back and start again is the kind of
 *  friction that makes a working list feel like a fixed one.
 */
export function QuickAddCustomer({ onDone, onCancel }: {
  onDone: (contact: Contact) => void
  onCancel: () => void
}) {
  const { push } = useToast()
  const [name, setName] = useState('')
  const [gstin, setGstin] = useState('')
  const [email, setEmail] = useState('')
  const [terms, setTerms] = useState(30)
  const [busy, setBusy] = useState(false)
  // Kept in the panel as well as the toast: a rejected GSTIN checksum needs to
  // stay on screen next to the field it is about, not fade after four seconds.
  const [error, setError] = useState<string | null>(null)

  const create = async () => {
    setBusy(true); setError(null)
    try {
      const contact = await biz.createContact({
        name, gstin: gstin.trim() || null, email: email.trim() || null,
        is_customer: true, is_vendor: false, payment_terms_days: terms,
      })
      push('success', 'Customer added', contact.name)
      onDone(contact)
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Something went wrong.'
      setError(msg)
      push('error', 'Could not add the customer', msg)
    } finally { setBusy(false) }
  }

  return (
    <div className="quick-add">
      <div className="quick-add-title">New customer</div>
      <div className="quick-add-grid">
        <label>
          <span>Name *</span>
          <input className="input" autoFocus value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Industries" />
        </label>
        <label>
          <span>GSTIN</span>
          <input className="input input-mono" maxLength={15} value={gstin}
            onChange={(e) => setGstin(e.target.value.toUpperCase())}
            placeholder="27AAACN4321F1ZV" />
        </label>
        <label>
          <span>Email</span>
          <input className="input" value={email}
            onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label>
          <span>Payment terms (days)</span>
          <input className="input" type="number" value={terms}
            onChange={(e) => setTerms(Number(e.target.value))} />
        </label>
      </div>
      <div className="quick-add-note">
        The GSTIN sets their state, which decides CGST + SGST or IGST. You can
        leave it blank now and add it later in Contacts.
      </div>
      {error && <div className="quick-add-error">{error}</div>}
      <div className="row">
        <button className="btn btn-primary btn-sm" onClick={create}
          disabled={busy || !name.trim()}>
          {busy && <span className="spinner" />}Add customer
        </button>
        <button className="btn btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

/** Same idea for products, from inside a line item. */
export function QuickAddProduct({ onDone, onCancel }: {
  onDone: (product: Product) => void
  onCancel: () => void
}) {
  const { push } = useToast()
  const [name, setName] = useState('')
  const [hsn, setHsn] = useState('')
  const [unit, setUnit] = useState('Nos')
  const [price, setPrice] = useState(0)
  const [rate, setRate] = useState(18)
  const [kind, setKind] = useState<'goods' | 'service'>('goods')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const create = async () => {
    setBusy(true); setError(null)
    try {
      const product = await biz.createProduct({
        name, hsn_sac: hsn.trim() || null, unit, kind,
        sale_price: price, gst_rate: rate,
      })
      push('success', 'Product added', product.name)
      onDone(product)
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Something went wrong.'
      setError(msg)
      push('error', 'Could not add the product', msg)
    } finally { setBusy(false) }
  }

  return (
    <div className="quick-add">
      <div className="quick-add-title">New product</div>
      <div className="quick-add-grid">
        <label>
          <span>Name *</span>
          <input className="input" autoFocus value={name}
            onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          <span>Type</span>
          <select className="input" value={kind}
            onChange={(e) => setKind(e.target.value as 'goods' | 'service')}>
            <option value="goods">Goods</option>
            <option value="service">Service</option>
          </select>
        </label>
        <label>
          <span>{kind === 'service' ? 'SAC code' : 'HSN code'}</span>
          <input className="input input-mono" value={hsn}
            onChange={(e) => setHsn(e.target.value)} placeholder="73181500" />
        </label>
        <label>
          <span>Unit</span>
          <select className="input" value={unit}
            onChange={(e) => setUnit(e.target.value)}>
            {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </label>
        <label>
          <span>Sale price</span>
          <input className="input" type="number" step="0.01" value={price}
            onChange={(e) => setPrice(Number(e.target.value))} />
        </label>
        <label>
          <span>GST rate</span>
          <select className="input" value={rate}
            onChange={(e) => setRate(Number(e.target.value))}>
            {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
          </select>
        </label>
      </div>
      {error && <div className="quick-add-error">{error}</div>}
      <div className="row">
        <button className="btn btn-primary btn-sm" onClick={create}
          disabled={busy || !name.trim()}>
          {busy && <span className="spinner" />}Add product
        </button>
        <button className="btn btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}
